import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowDownWideNarrow, Bell, BellRing, BookOpen, Bookmark, ChevronRight, Clock, GraduationCap, Hash, Heart, Newspaper, PenLine,
} from 'lucide-react';
import { articleService, tagService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useCourse } from '../contexts/CourseContext';
import {
  Breadcrumbs, Button, EmptyState, GlobalShell, Loader, PageHeader, SectionTitle, Tabs,
} from '../components/ui';
import { ArticleRow, TagPill } from '../components/articles/ArticleBits';
import './Articles.css';

/** Página do tema: seguir, contadores, feed do tema e, na lateral, tópicos ligados e temas relacionados. */
function ArticleTag() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { courses } = useCourse();
  const [tag, setTag] = useState(null);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState('all');
  const [course, setCourse] = useState('');
  const [sort, setSort] = useState('recent');
  const [list, setList] = useState(null);
  const [savedCount, setSavedCount] = useState(null);
  const [following, setFollowing] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setError(false);
    setTag(null);
    tagService.get(slug)
      .then(t => { setTag(t); setFollowing(t.following); })
      .catch(() => setError(true));
    articleService.list({ tab: 'saved', tag: slug, size: 1 }).then(r => setSavedCount(r.total)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    setList(null);
    articleService.list({ tab, tag: slug, course: course || undefined, sort, size: 30 })
      .then(setList)
      .catch(() => setList({ items: [] }));
  }, [slug, tab, course, sort]);

  const toggleFollow = async () => {
    setBusy(true);
    try {
      const r = await tagService.toggleFollow(slug);
      setFollowing(r.following);
      setTag(t => ({ ...t, followers: r.followers }));
    } finally {
      setBusy(false);
    }
  };

  const open = (a) => navigate(`/artigos/${a.slug}`);

  if (error) {
    return (
      <GlobalShell>
        <EmptyState
          file="tema.xml"
          title="Tema não encontrado"
          description="Ele pode ter sido renomeado ou ainda não tem artigos."
          action={<Button icon={Newspaper} onClick={() => navigate('/artigos')}>Ver todos os artigos</Button>}
        />
      </GlobalShell>
    );
  }
  if (!tag) {
    return <GlobalShell><Loader label="Carregando tema..." /></GlobalShell>;
  }

  const listCount = list?.items ? (list.total ?? list.items.length) : null;

  return (
    <GlobalShell>
      <div className="ss-page ss-page--wide ss-page--crumbs arts-page">
        <Breadcrumbs items={[
          { label: 'Artigos', onClick: () => navigate('/artigos') },
          { label: 'Temas', onClick: () => navigate('/artigos') },
          { label: tag.name },
        ]} />
        <PageHeader
          icon={Hash}
          eyebrow="Tema"
          title={tag.name}
          subtitle={`Artigos da turma sobre ${tag.name}.`}
          actions={!user?.demo && (
            <>
              <Button variant="outline" icon={following ? BellRing : Bell} onClick={toggleFollow} disabled={busy}>
                {following ? 'Seguindo' : 'Seguir tema'}
              </Button>
              <Button icon={PenLine} onClick={() => navigate('/artigos/novo', { state: { tags: [tag.name] } })}>
                Escrever sobre {tag.name}
              </Button>
            </>
          )}
        />

        <div className="artt-counts">
          <span><strong>{tag.articles}</strong>{tag.articles === 1 ? 'artigo' : 'artigos'}</span>
          <span><strong>{tag.authors}</strong>{tag.authors === 1 ? 'autor' : 'autores'}</span>
          <span><strong>{tag.followers}</strong>{tag.followers === 1 ? 'seguidor' : 'seguidores'}</span>
        </div>

        <div className="arts-bar">
          <Tabs
            tabs={[
              { value: 'all', label: 'Todos', icon: Newspaper, count: tag.articles },
              { value: 'saved', label: 'Salvos', icon: Bookmark, count: savedCount ?? undefined },
            ]}
            value={tab}
            onChange={setTab}
          />
          <div className="artt-selects">
            <label className="arts-select">
              <GraduationCap size={13} aria-hidden="true" />
              <select value={course} onChange={(e) => setCourse(e.target.value)} aria-label="Curso">
                <option value="">Qualquer curso</option>
                {courses.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                <option value="none">Sem curso vinculado</option>
              </select>
            </label>
            <label className="arts-select">
              <ArrowDownWideNarrow size={13} aria-hidden="true" />
              <select value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Ordenar">
                <option value="recent">Mais recentes</option>
                <option value="popular">Mais curtidos</option>
              </select>
            </label>
          </div>
        </div>

        <div className="arts-grid">
          <section className="arts-main">
            <SectionTitle
              icon={tab === 'saved' ? Bookmark : sort === 'popular' ? Heart : Clock}
              aside={listCount != null && `${listCount} ${listCount === 1 ? 'artigo' : 'artigos'}`}
            >
              {tab === 'saved' ? 'Salvos' : sort === 'popular' ? 'Mais curtidos' : 'Recentes'}
            </SectionTitle>
            {!list ? (
              <Loader label="Carregando artigos..." />
            ) : list.items.length === 0 ? (
              <EmptyState
                file="tema.xml"
                title={tab === 'saved' ? 'Nenhum artigo salvo neste tema' : 'Nenhum artigo com esses filtros'}
                description={tab === 'saved' ? 'Use o marcador ao lado de um artigo para guardar e ler depois.' : 'Tente outro curso.'}
              />
            ) : (
              <div className="arts-feed">
                {list.items.map(a => <ArticleRow key={a.id} article={a} onOpen={open} />)}
              </div>
            )}
          </section>

          <aside className="arts-side">
            <h2 className="arts-side__title">Aparece também em</h2>
            <p className="artt-side__hint">Tópicos que usam este tema mostram estes artigos na aba Artigos.</p>
            {tag.topics.length === 0 ? (
              <p className="arts-side__empty">Nenhum tópico ligado a este tema ainda.</p>
            ) : (
              <div className="arts-courses">
                {tag.topics.map(t => {
                  const color = courses.find(c => c.slug === t.courseSlug)?.color;
                  return (
                    <button
                      key={t.topicId}
                      type="button"
                      className="arts-course"
                      onClick={() => navigate(`/curso/${t.courseSlug}/estudo/${t.topicId}`, { state: { tab: 'articles' } })}
                    >
                      <span className="arts-course__icon" style={{ background: color || 'var(--brand)' }}><BookOpen size={14} aria-hidden="true" /></span>
                      <span className="arts-course__text">
                        <strong>{t.name}</strong>
                        <span>{t.courseName} · Fase {t.fase}</span>
                      </span>
                      <ChevronRight size={14} aria-hidden="true" />
                    </button>
                  );
                })}
              </div>
            )}
            {tag.related.length > 0 && (
              <div className="artt-related">
                <h2 className="arts-side__title">Temas relacionados</h2>
                <div className="arts-side__tags">
                  {tag.related.map(t => (
                    <TagPill key={t.slug} size="sm" tag={t.name} count={t.count} onClick={() => navigate(`/artigos/tema/${t.slug}`)} />
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </GlobalShell>
  );
}

export default ArticleTag;
